/*----------------------------------
- HOME PLATFORM SECTION
- "One toolbox. The whole job." Six capability cards for what ships beside the buy
- path rather than on it. Deliberately unlike the workflow bento above it: no step
- numbers and no motion canvases, because numbering these would read as a sequence.
- Each card is a centred column, so the six read as a set of equals.
----------------------------------*/

import React from 'react';

import Icon from '@/client/components/ui/icon/Icon';
import WebsiteSupportingLink from '@uniquedomains/components/website/WebsiteSupportingLink';
import {
    HOME_PLATFORM_CAPABILITIES,
    HOME_PLATFORM_SECTION_COPY,
    type HomePlatformCapability,
} from '@/client/catalogs/landing/platformCapabilities';
import { PAGE_CONTAINER_CLASS } from '@/client/pages/landing/components/shared/pageStyles';

/*----------------------------------
- CONSTANTS
----------------------------------*/

const CARD_TITLE_CLASS = 'text-[19px] font-semibold leading-[1.25] tracking-[-0.015em] text-slate-900';
const CARD_TITLE_LINK_CLASS =
    'rounded-sm underline-offset-4 transition hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400';
const CARD_BODY_CLASS = 'mt-2.5 text-[14.5px] leading-[1.6] text-slate-600';
const COMING_SOON_CLASS =
    'inline-flex rounded-full bg-[#E8E5DD] px-[9px] py-[3px] text-[10px] font-bold uppercase tracking-[0.06em] text-[#5C5F61]';

/**
 * One accent per card, read by the icon through `--ud-product-accent`, so colouring
 * six icons needs no per-card CSS rule. `--ud-product-accent-soft` is set alongside it
 * because the differentiation cards above pair the two, and keeping the contract
 * identical means a future tinted surface here needs no new plumbing.
 */
const getCapabilityCardStyle = (capability: HomePlatformCapability): React.CSSProperties =>
    ({
        '--ud-product-accent': capability.accent,
        '--ud-product-accent-soft': capability.accentSoft,
    }) as React.CSSProperties;

/*----------------------------------
- COMPONENT
----------------------------------*/

type HomePlatformSectionProps = {
    pricingHref: string;
    onCapabilityClick?: (capability: HomePlatformCapability) => void;
    onPricingClick?: () => void;
};

const HomePlatformSection = ({ pricingHref, onCapabilityClick, onPricingClick }: HomePlatformSectionProps) => (
    <section id="platform" class={`${PAGE_CONTAINER_CLASS} pt-[72px] lg:pt-32`}>
        {/* The phone mockup does not draw this section, so it follows the workflow
            block above it by analogy: left-aligned heading, the same tinted panel. */}
        <div class="lg:mx-auto lg:max-w-[680px] lg:text-center">
            <h2 class="text-pretty text-[30px] font-medium leading-[1.1] tracking-[-0.03em] text-slate-900 lg:text-balance lg:text-[clamp(2.125rem,3.2vw,2.875rem)]">
                {HOME_PLATFORM_SECTION_COPY.heading}
            </h2>
            <p class="mt-4 text-base leading-[1.6] text-slate-600 lg:mx-auto lg:mt-[18px] lg:text-[16.5px] lg:leading-[1.65]">
                {HOME_PLATFORM_SECTION_COPY.subheading}
            </p>
        </div>

        <div class="ud-landing-card-shell ud-landing-value-card-grid is-six mt-7 lg:mt-[52px]">
            {HOME_PLATFORM_CAPABILITIES.map((capability) => (
                <article
                    key={capability.id}
                    data-reveal
                    data-testid={`home-platform-${capability.id}`}
                    class="ud-landing-value-card ud-landing-capability-card"
                    style={getCapabilityCardStyle(capability)}
                >
                    <Icon name={capability.iconName} size={30} class="ud-landing-capability-card__icon" />
                    <span class="ud-landing-capability-card__pill-slot">
                        {capability.comingSoon ? <span class={COMING_SOON_CLASS}>Coming soon</span> : null}
                    </span>
                    <h3 class={CARD_TITLE_CLASS}>
                        {capability.href ? (
                            <a
                                href={capability.href}
                                class={CARD_TITLE_LINK_CLASS}
                                onClick={() => onCapabilityClick?.(capability)}
                            >
                                {capability.title}
                            </a>
                        ) : (
                            capability.title
                        )}
                    </h3>
                    <p class={CARD_BODY_CLASS}>{capability.body}</p>
                </article>
            ))}
        </div>

        {/* A supporting link, not a second accent pill: the bento already closes with
            "Start with my brief", and a second primary CTA this close would split the click. */}
        <div class="mt-10 flex justify-center">
            <WebsiteSupportingLink href={pricingHref} size="md" onClick={onPricingClick}>
                See what it costs
            </WebsiteSupportingLink>
        </div>
    </section>
);

export default HomePlatformSection;
